/**
 * Usage upload, utilisation, forecast and alerts — ADR-001 §7.4, §8.3, §10.1, §11.7, guide §5.4, §6.5, §7.
 *
 * Visibility: managers see every client; an ops_member sees only clients they are the current SPOC of;
 * requesters and HR see nothing (403 on collections, 404 by id). Revenue (`mrr_lakh`) is absent for
 * non-managers and is never part of a usage payload; it only orders a manager's alert list (ADR §8.3).
 */
import { z } from 'zod';
import { IsoDate, Lakh, PageQuery, Percent, Timestamp, Uuid, derived, list, page } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
import { ClientName } from './requests.js';
const Period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM, for example 2026-10');
/** Where a product's numbers come from (prototype SOURCES): automatic API pull, a weekly export, or typed in. */
export const UsageSource = z.enum(['api', 'csv', 'manual']).meta({ id: 'UsageSource' });
export const USAGE_SOURCE_LABEL = {
    api: { label: 'Automatic', help: 'Pulled from the product every day. Nobody types anything.', limit_days: 1 },
    csv: { label: 'Weekly export', help: 'The SPOC uploads the product’s export once a week.', limit_days: 7 },
    manual: { label: 'Typed in', help: 'The SPOC types the number in once a week.', limit_days: 7 },
};
/** Chip tones for utilisation (prototype uChip): below the low line, below the watch line, good, no data. */
export const UsageTone = z.enum(['cold', 'warm', 'hot', 'info']).meta({ id: 'UsageTone' });
/** Prototype feedState: is the data arriving on time for this client-product? */
export const FeedState = z
    .object({
    kind: z.enum(['waiting', 'no_data', 'late', 'fresh']),
    tone: UsageTone,
    /** "Waiting for first data" · "No data for 9 days" · "Late by 2 days" · "Fresh today" · "Updated 3d ago". */
    label: z.string(),
    days_since_last: z.number().int().nonnegative().nullable(),
})
    .meta({ id: 'FeedState' });
// ============================================================================ products
export const ProductField = z
    .object({
    id: Uuid,
    field_key: z.string(),
    label: z.string(),
    unit: z.string().nullable(),
    /** The main metric (the number utilisation is computed from). */
    is_main: z.boolean(),
    /** `false` marks fields the guide flags as not final (e.g. Contact Database). */
    is_confirmed: z.boolean(),
    /** Accepted report headers for this field, normalised (lower case, single spaces). */
    aliases: z.array(z.string()),
})
    .meta({ id: 'ProductField' });
export const Product = z
    .object({
    key: z.string(),
    name: z.string(),
    kind: z.string(),
    main_metric: z.string(),
    unit: z.string(),
    period: z.enum(['month', 'week']),
    source: UsageSource,
    low_line: Percent,
    /** At least `max(60, low_line + 10)` (prototype rule); low_line is capped at 90 so this stays within 100. */
    watch_line: Percent,
    fall_pct: Percent,
    freshness_limit_days: z.number().int().positive(),
    sort_order: z.number().int(),
    fields: z.array(ProductField),
    /** Set on a save that adjusted watch_line, so the UI can explain it ("Raised to 60 to stay above the low line"). */
    adjustment: z.object({ watch_line_requested: Percent, watch_line_saved: Percent, reason: z.string() }).nullable(),
})
    .meta({ id: 'Product' });
export const ProductList = list(Product).meta({ id: 'ProductList' });
export const ProductKeyParams = z.object({ key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/) });
/**
 * The low-use line. Capped at 90 so the watch line (at least low_line + 10) can never pass 100 — a larger
 * value is a 422 with a plain message, never a 500 on the response (review t-7100df5f).
 */
const LowLine = z.number().min(0).max(90, 'The low-use line can be at most 90%, so the watch line (10 above it) stays within 100%');
const FieldIn = z
    .object({
    field_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
    label: z.string().trim().min(2).max(60),
    unit: z.string().trim().max(20).optional(),
    is_main: z.boolean().optional(),
    is_confirmed: z.boolean().optional(),
})
    .strict();
export const CreateProductBody = z
    .object({
    key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'Use lower-case letters, digits and _'),
    name: z.string().trim().min(2).max(60),
    kind: z.string().trim().min(2).max(30),
    main_metric: z.string().trim().min(2).max(60),
    unit: z.string().trim().min(1).max(20),
    period: z.enum(['month', 'week']).default('month'),
    source: UsageSource,
    low_line: LowLine.default(40),
    /** Saved as max(this, 60, low_line + 10); `adjustment` in the response says when it was raised. */
    watch_line: Percent.default(60),
    fall_pct: Percent.default(15),
    freshness_limit_days: z.number().int().min(1).max(60).optional(),
    fields: z.array(FieldIn).max(20).optional(),
})
    .strict();
/** Thresholds and names are editable in Settings; `fields` replaces the extra-number list (the main field is kept). */
export const UpdateProductBody = z
    .object({
    name: z.string().trim().min(2).max(60).optional(),
    main_metric: z.string().trim().min(2).max(60).optional(),
    unit: z.string().trim().min(1).max(20).optional(),
    source: UsageSource.optional(),
    low_line: LowLine.optional(),
    watch_line: Percent.optional(),
    fall_pct: Percent.optional(),
    freshness_limit_days: z.number().int().min(1).max(60).optional(),
    fields: z.array(FieldIn).max(20).optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
export const FieldAlias = z
    .object({ id: Uuid, product_field_id: Uuid, field_label: z.string(), alias: z.string(), alias_normalised: z.string() })
    .meta({ id: 'FieldAlias' });
export const FieldAliasList = list(FieldAlias).meta({ id: 'FieldAliasList' });
/** "Page Views " and "page-views" both normalise to "page views" (lower case, non-alphanumeric runs → one space). */
export const CreateAliasBody = z.object({ product_field_id: Uuid, alias: z.string().trim().min(1).max(80) }).strict();
export const AliasParams = z.object({ key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/), id: Uuid });
/** The prototype's header normaliser, shared so the UI can preview what an alias will match. */
export const normaliseHeader = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// ============================================================================ client products
/**
 * The largest usage figure stored: main_value numeric(16,2) on usage_log_entries and usage_report_rows. The one bound
 * for a typed-in figure (ManualUsageBody) and an uploaded report's row (engine stageRows: "Number too large").
 */
export const USAGE_AMOUNT_MAX = 99_999_999_999_999.99;
/**
 * The largest amounts the columns hold (S5-MN-03): plan/base numeric(14,2), mrr in lakh numeric(12,2), a usage
 * main value numeric(16,2). Larger numbers are a 422 on the field, never a database overflow.
 */
const PlanAmount = z.number().min(0).max(999_999_999_999.99);
const MrrLakh = Lakh.max(9_999_999_999.99);
const UsageAmount = z.number().min(0).max(USAGE_AMOUNT_MAX);
export const ClientProductParams = z.object({ id: Uuid, cp_id: Uuid });
/** A member sending `mrr_lakh` gets 403 with field_errors (revenue is manager-only). */
export const CreateClientProductBody = z
    .object({
    product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
    plan: PlanAmount,
    base: PlanAmount.optional(),
    mrr_lakh: MrrLakh.optional(),
    /** Overrides the product's default source for this client (prototype: some clients type numbers in). */
    source: UsageSource.optional(),
    started_on: IsoDate.optional(),
})
    .strict();
export const UpdateClientProductBody = z
    .object({
    plan: PlanAmount.optional(),
    base: PlanAmount.nullable().optional(),
    mrr_lakh: MrrLakh.nullable().optional(),
    source: UsageSource.nullable().optional(),
    status: z.enum(['active', 'paused', 'ended']).optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
// ============================================================================ overview (heat grid) and client usage page
export const UsageCell = z
    .object({
    product_key: z.string(),
    /**
     * % of plan used so far this period; `null` when there is no data. Over 100 is normal ("Over plan: upsell?"),
     * so this is NOT built on Percent: in Zod 4 a second .max() adds a check instead of replacing the first.
     */
    util: z.number().min(0).max(400).nullable(),
    /** % vs the client's own baseline (may be negative). */
    trend: z.number().nullable(),
    tone: UsageTone,
    feed_state: FeedState,
    /** Main-metric total so far this period; `null` when there is no data. Tooltip: "Pageviews: {used} of {plan}." */
    used: z.number().nonnegative().nullable(),
    /** The client's plan for the period; `null` when none is set. */
    plan: z.number().nonnegative().nullable(),
    /** Last month's main-metric total (the tooltip's "Last month Z"); `null` when unknown. */
    base: z.number().nonnegative().nullable(),
    /**
     * Forecast % of plan in 30 days (UsageForecast.util_in_30d); `null` when there is no forecast OR the fit is too
     * uneven to draw (`UsageForecast.meaningful` false: r² under settings `forecast.min_r2`).
     */
    util_in_30d: z.number().min(0).max(160).nullable(),
    /**
     * The red "!" (prototype `pd`): the forecast falls under the low line in 30 days while utilisation is still at or
     * above it today (`util_in_30d < low_line && util >= low_line`). Always false when either number is `null` —
     * so false on a fit too uneven to forecast.
     */
    predicted_below_low: z.boolean(),
})
    .meta({ id: 'UsageCell' });
export const UsageOverview = z
    .object({
    as_of: IsoDate,
    /** `main_metric` names the number in the cell tooltip ("Pageviews: X of Y"). */
    products: z.array(z.object({ key: z.string(), name: z.string(), main_metric: z.string() })),
    /** Rows: every client in scope; a product the client does not buy has no cell. */
    clients: z.array(z.object({ client: ClientRef, cells: z.array(UsageCell) })),
})
    .meta({ id: 'UsageOverview' });
export const UsageOverviewQuery = z.object({
    segment: z.enum(['BFSI', 'QSR', 'Retail']).optional(),
    product_key: z.string().optional(),
});
/** Guide §6.5 / ADR §8.3: least squares over 14 days, slope damped 0.6, projected 30 days, capped at 160%. */
export const UsageForecast = derived(z.object({
    util_in_30d: z.number().min(0).max(160),
    /** Days until utilisation reaches the low line at the damped slope; `null` when not falling or already below. */
    days_to_low_line: z.number().int().nonnegative().nullable(),
    forecast_abs: z.number().nonnegative(),
    /**
     * Fit quality; the UI hides a meaningless line (low r²). Never NaN: a series whose values are all equal is
     * fitted exactly by a flat line, so r2 = 1. With fewer than 2 points there is no forecast (`forecast: null`).
     */
    r2: z.number().min(0).max(1),
    /**
     * `r2 >= forecast.min_r2` (Settings → thresholds, default 0.3). When false the fit is too noisy to mean anything:
     * the UI hides the forecast line and the 30-day figure.
     */
    meaningful: z.boolean(),
})).meta({ id: 'UsageForecast' });
export const ClientProductUsage = z
    .object({
    product_key: z.string(),
    product_name: z.string(),
    main_metric: z.string(),
    unit: z.string(),
    source: UsageSource,
    plan: z.number().nonnegative().nullable(),
    used: z.number().nonnegative().nullable(),
    util: z.number().nullable(),
    /**
     * Last month's main-metric total — the same figure as UsageCell.base and `months.at(-1).value`: the real report
     * if one is in, else the estimate from the client's usual (client_products.base). `null` when neither exists.
     * The configured baseline itself is ClientProduct.base (GET /clients/{id}/products).
     */
    base: z.number().nonnegative().nullable(),
    /** % vs the client's usual (client_products.base), else vs last month's real figure. */
    trend: z.number().nullable(),
    /** % change against last month's total. */
    vs_last_month: z.number().nullable(),
    low_line: Percent,
    watch_line: Percent,
    forecast: UsageForecast.nullable(),
    /**
     * Always exactly six past months, oldest first, so charts across products align; a month with no report has
     * `value`/`util` `null`. Until real reports exist months are estimated — the UI must label them.
     */
    months: z
        .array(z.object({ period: Period, value: z.number().nullable(), util: z.number().nullable(), is_estimated: z.boolean() }))
        .length(6),
    /** Always exactly 14 days ending `as_of`, oldest first; a day without data has `util: null, no_data: true`. */
    days: z
        .array(z.object({ day: IsoDate, util: z.number().nullable(), no_data: z.boolean(), is_estimated: z.boolean() }))
        .length(14),
    /** The other recorded numbers for this period (product extras). */
    extras: z.array(z.object({ field_key: z.string(), label: z.string(), unit: z.string().nullable(), value: z.number().nullable() })),
    /** The one-line tip (prototype TIPS: low / falling / strong). */
    tip: z.string(),
    feed_state: FeedState,
})
    .meta({ id: 'ClientProductUsage' });
export const ClientUsage = z
    .object({ client: ClientRef, as_of: IsoDate, products: z.array(ClientProductUsage) })
    .meta({ id: 'ClientUsage' });
export const ClientUsageQuery = z.object({ product_key: z.string().optional() });
export const ClientIdOnly = z.object({ id: Uuid });
/** Most clients `GET /client-products?client_ids=` may name at once. */
export const MAX_CLIENT_PRODUCTS_BATCH = 200;
/** Most clients `GET /usage/clients?client_ids=` may name at once (the same cap). */
export const MAX_CLIENT_USAGE_BATCH = MAX_CLIENT_PRODUCTS_BATCH;
/** `?client_ids=`: a comma list of client ids, at most 200, repeats dropped; empty is the same as absent. */
const ClientIdsCsv = z
    .string()
    .max(MAX_CLIENT_PRODUCTS_BATCH * 37 + 100)
    .transform((s, ctx) => {
    const parts = [...new Set(s.split(',').map((p) => p.trim()).filter(Boolean))];
    if (parts.length > MAX_CLIENT_PRODUCTS_BATCH) {
        ctx.addIssue({ code: 'custom', message: `At most ${MAX_CLIENT_PRODUCTS_BATCH} clients at once (got ${parts.length}).` });
        return z.NEVER;
    }
    const bad = parts.filter((p) => !Uuid.safeParse(p).success);
    if (bad.length) {
        const shown = bad.slice(0, 5).map((p) => (p.length > 40 ? `${p.slice(0, 40)}…` : p));
        ctx.addIssue({ code: 'custom', message: `Not a client id: ${shown.join(', ')}.` });
        return z.NEVER;
    }
    return parts;
})
    .optional();
/**
 * `GET /client-products` (t-c1371dd8): `?client_ids` is a comma list of client ids, at most 200, repeats dropped. Without
 * it (or empty) the read is every ACTIVE client the caller can see, which is what Settings → Products and usage lists.
 */
export const ClientProductsBatchQuery = z.object({ client_ids: ClientIdsCsv });
/**
 * `GET /usage/clients` (t-b083972d): `?client_ids` as for GET /client-products (comma list, at most 200, repeats
 * dropped). Without it (or empty) the read is every ACTIVE client the caller can see: the clients of GET /usage/overview.
 */
export const ClientUsagesQuery = z.object({ client_ids: ClientIdsCsv });
// ============================================================================ predictive alerts
export const AlertRule = z
    .enum(['no_data_reported', 'down_15_vs_last_month', 'util_below_low_line', 'forecast_below_low_line', 'forecast_below_watch_falling', 'four_months_decline'])
    .meta({ id: 'AlertRule' });
export const AlertSeverity = z.enum(['at_risk', 'watch', 'info']).meta({ id: 'AlertSeverity' });
export const Alert = z
    .object({
    id: Uuid,
    client: ClientRef,
    product_key: z.string(),
    product_name: z.string(),
    rule: AlertRule,
    severity: AlertSeverity,
    /** The plain-English why (the rule, in words). */
    why: z.string(),
    /** The figures the card shows (util, trend, util_in_30d, low_line, months…). Never revenue. */
    numbers: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
    as_of: IsoDate,
    /**
     * Worst-first ordering score. For managers: priority_weight×3 + (at_risk ? 4 : 2) + mrr/2 + (forecast ? 1 : 0).
     * For members the mrr term is dropped (revenue is hidden) — a documented difference, not a bug (ADR §8.3).
     */
    score: z.number(),
    state: z.enum(['open', 'acknowledged', 'resolved']),
    acked_by: UserRef.nullable(),
    acked_at: Timestamp.nullable(),
    note: z.string().nullable(),
})
    .meta({ id: 'Alert' });
export const AlertPage = page(Alert).meta({ id: 'AlertPage' });
export const ListAlertsQuery = PageQuery.extend({
    state: z.enum(['open', 'acknowledged', 'resolved']).default('open'),
    severity: AlertSeverity.optional(),
    client_id: Uuid.optional(),
    product_key: z.string().optional(),
});
export const AlertIdParams = z.object({ id: Uuid });
export const AlertActionBody = z.object({ note: z.string().trim().max(500).optional() }).strict();
// ============================================================================ report upload (guide §7, ADR §10.1)
export const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** A product report — the file the worker reads. */
export const UPLOAD_EXTENSIONS = ['.xlsx', '.csv', '.tsv', '.txt'];
/**
 * Proof attached to a typed-in number (prototype usageModal: "PDF, Excel, CSV or screenshot"). Kept, never read.
 */
export const ATTACHMENT_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', ...UPLOAD_EXTENSIONS];
/**
 * The content types an attachment may be stored under (the signed PUT binds it). Nothing a browser would run
 * (no HTML, SVG or script), so a stored attachment can never become a page.
 */
export const ATTACHMENT_CONTENT_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'text/csv',
    'text/plain',
    'text/tab-separated-values',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/octet-stream',
];
/**
 * `report`: a product's usage report, read by POST /usage-reports/{id}/parse. `attachment`: a proof file for
 * POST /usage/manual (`file_key`) — never parsed, never listed as a report, not addressable under /usage-reports/{id}.
 */
export const UploadPurpose = z.enum(['report', 'attachment']).meta({ id: 'UploadPurpose' });
const hasExt = (f, exts) => exts.some((e) => f.toLowerCase().endsWith(e));
/**
 * Ask for an upload slot. 422 over 10 MB or on an extension the purpose does not take (report: .xlsx .csv .tsv
 * .txt; attachment: also .pdf .png .jpg .jpeg, with a content type from ATTACHMENT_CONTENT_TYPES). The month is
 * chosen here and never read from the file (prototype rule). Only the current month updates live utilisation;
 * earlier months are logged.
 */
export const SignUploadBody = z
    .object({
    product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
    period: Period,
    /** Omitted: `report` (the upload modal). The Type-in modal's attach sends `attachment`. */
    purpose: UploadPurpose.default('report'),
    filename: z.string().trim().min(5).max(200),
    bytes: z.number().int().positive().max(UPLOAD_MAX_BYTES, 'The file is over 10 MB. Split it or remove extra sheets.'),
    content_type: z.string().max(120),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.purpose === 'report') {
        if (!hasExt(b.filename, UPLOAD_EXTENSIONS))
            ctx.addIssue({ code: 'custom', path: ['filename'], message: 'Upload an .xlsx, .csv, .tsv or .txt file' });
        return;
    }
    if (!hasExt(b.filename, ATTACHMENT_EXTENSIONS)) {
        ctx.addIssue({ code: 'custom', path: ['filename'], message: 'Attach a PDF, Excel, CSV or screenshot (.png or .jpg)' });
    }
    if (!ATTACHMENT_CONTENT_TYPES.includes(b.content_type.toLowerCase().split(';')[0].trim())) {
        ctx.addIssue({ code: 'custom', path: ['content_type'], message: 'This kind of file cannot be attached' });
    }
});
export const SignUploadResponse = z
    .object({
    upload_id: Uuid,
    /** Where the browser sends the file directly (never through the API). */
    url: z.string(),
    method: z.enum(['PUT', 'POST']),
    /** Form fields or headers the upload must carry. */
    fields: z.record(z.string(), z.string()),
    spaces_key: z.string(),
    expires_at: Timestamp,
})
    .meta({ id: 'SignUploadResponse' });
export const UploadStatus = z.enum(['awaiting_file', 'parsing', 'staged', 'applied', 'discarded', 'failed']).meta({ id: 'UploadStatus' });
export const Upload = z
    .object({
    id: Uuid,
    product_key: z.string(),
    period: Period,
    original_filename: z.string(),
    bytes: z.number().int(),
    uploaded_by: UserRef,
    uploaded_at: Timestamp,
    status: UploadStatus,
    rows_total: z.number().int().nonnegative(),
    rows_matched: z.number().int().nonnegative(),
    rows_skipped: z.number().int().nonnegative(),
    parse_error: z.string().nullable(),
})
    .meta({ id: 'Upload' });
export const UploadIdParams = z.object({ id: Uuid });
export const MatchKind = z.enum(['exact', 'fuzzy', 'ambiguous', 'none']).meta({ id: 'MatchKind' });
export const UploadRow = z
    .object({
    row_number: z.number().int().positive(),
    /** The row as read from the file. */
    raw: z.record(z.string(), z.string()),
    matched_client: ClientName.nullable(),
    /** For `ambiguous`: the clients above the similarity floor — the reviewer picks one. */
    candidates: z.array(ClientName),
    match_kind: MatchKind,
    match_score: z.number().min(0).max(1).nullable(),
    main_value: z.number().nullable(),
    extras: z.record(z.string(), z.number().nullable()),
    /** "Client not found" · "{client} has not bought {product}" · "No numbers" · "Number too large" · "Not one of your clients". */
    unmatched_reason: z.string().nullable(),
    decision: z.enum(['pending', 'apply', 'skip']),
})
    .meta({ id: 'UploadRow' });
export const UploadDetail = z
    .object({
    upload: Upload,
    rows: z.array(UploadRow),
    summary: z.object({
        total: z.number().int(), exact: z.number().int(), fuzzy: z.number().int(), ambiguous: z.number().int(), none: z.number().int(),
        will_apply: z.number().int(), will_skip: z.number().int(),
    }),
})
    .meta({ id: 'UploadDetail' });
export const ParseResponse = z.object({ status: z.literal('parsing') }).meta({ id: 'ParseResponse' });
/**
 * Apply the reviewed rows. Idempotent: re-applying the same period overwrites the same (client, product,
 * period) entries. `client_id` resolves an ambiguous row (must be one of its candidates). A member applies
 * rows for their own clients only.
 */
export const ApplyUploadBody = z
    .object({
    decisions: z
        .array(z.object({ row_number: z.number().int().positive(), decision: z.enum(['apply', 'skip']), client_id: Uuid.optional() }).strict())
        .max(5000),
})
    .strict();
export const ApplyUploadResponse = z
    .object({
    applied: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Only the current month updates live utilisation; earlier months feed the 6-month chart. */
    period_is_current: z.boolean(),
    clients_recomputing: z.array(Uuid),
})
    .meta({ id: 'ApplyUploadResponse' });
export const ListUploadsQuery = PageQuery.extend({ product_key: z.string().optional(), period: Period.optional() });
/** Prototype Reports screen states per product: "In this month", "Report due", "Not uploaded yet". */
export const UploadsPage = page(Upload)
    .extend({
    products: z.array(z.object({
        product_key: z.string(),
        last_period: Period.nullable(),
        last_uploaded_at: Timestamp.nullable(),
        state: z.enum(['in_this_month', 'report_due', 'not_uploaded']),
        label: z.string(),
        /** Client-products for this product in the caller's scope (active or paused). */
        clients: z.number().int().nonnegative(),
        /** Who entered the latest numbers for this product; `null` when none yet or pulled automatically. */
        last_uploaded_by: UserRef.nullable(),
        /** The latest entry's file name; `null` when it was typed in or there is none yet. */
        last_filename: z.string().nullable(),
    })),
})
    .meta({ id: 'UploadsPage' });
// ============================================================================ the typed-in path
/**
 * `file_key`, when given, must be a completed upload made by the caller (otherwise 422): normally one signed with
 * `purpose: 'attachment'` (PDF, Excel, CSV or screenshot); a report upload of the caller's is also accepted.
 */
export const ManualUsageBody = z
    .object({
    client_id: Uuid,
    product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
    period: Period,
    main_value: UsageAmount,
    extras: z.record(z.string(), z.number().min(0)).optional(),
    file_key: z.string().max(500).optional(),
})
    .strict();
export const UsageLogEntry = z
    .object({
    id: Uuid,
    client: ClientName,
    product_key: z.string(),
    period: Period,
    main_value: z.number(),
    extras: z.record(z.string(), z.number().nullable()),
    source: z.enum(['upload', 'api', 'manual']),
    uploaded_by: UserRef.nullable(),
    at: Timestamp,
    upload_id: Uuid.nullable(),
    /** The report file the numbers came from (Reports → "Report file"); `null` = typed in or pulled automatically. */
    original_filename: z.string().nullable(),
})
    .meta({ id: 'UsageLogEntry' });
/** Reports → "Reports entered": newest first. */
export const UsageLogQuery = PageQuery.extend({ product_key: z.string().optional(), client_id: Uuid.optional() });
export const UsageLogPage = page(UsageLogEntry).meta({ id: 'UsageLogPage' });
export const TemplateParams = z.object({ product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/) });
/** `files.devPut` (non-production): the signed, expiring token the local file store hands out in place of a presigned URL. */
export const DevFileParams = z.object({ token: z.string().min(10).max(2000).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/) });
//# sourceMappingURL=usage.js.map